import { envConfig } from '@src/utils/envConfig';
import { FetchError, logger, Post } from '@talos/kyoko';
import { useCallback, useEffect, useState, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { IFrameLoading } from '../components/IFrameLoading';
import { useIFrameToken } from '../hooks/useIFrameToken';

interface IFrameAuthHandlerProps extends PropsWithChildren {
  /**
   * Callback to call after successful token exchange.
   * In production, this will call window.location.reload().
   * Useful for testing.
   */
  onReload: () => void;
  /**
   * Function to check if running in an IFrame.
   * Useful for testing.
   */
  isInIFrame: () => boolean;
}

/**
 * Component that handles IFrame authentication by receiving access_token via PostMessage
 * from the parent window and exchanging it for a session when running in an IFrame
 */
export const IFrameAuthHandler: React.FC<IFrameAuthHandlerProps> = ({ children, onReload, isInIFrame }) => {
  const navigate = useNavigate();
  const [originalUrl] = useState(() => window.location.href);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [exchangeError, setExchangeError] = useState<string>();

  const { token, error: tokenError, hasValidSession, shouldWaitForToken } = useIFrameToken(isInIFrame, originalUrl);

  // Combine token retrieval errors and exchange errors
  const error = tokenError || exchangeError;

  const fallbackToSilentLogin = useCallback(() => {
    const silentLoginUrl = envConfig.VITE_AUTH_SILENT_LOGIN_URL;
    if (silentLoginUrl) {
      // Falling back to silent-login for IFrame authentication
      // Redirect to silent-login URL which should handle IFrame authentication
      logger.info('[IFrameAuthHandler] Falling back to silent-login URL');
      window.location.href = silentLoginUrl;
    } else {
      // No silent-login URL configured, redirecting to normal login
      // Last resort: redirect to normal login
      logger.warn('[IFrameAuthHandler] No silent-login URL configured, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  const exchangeTokenForSession = useCallback(async () => {
    setIsExchangingToken(true);
    setExchangeError(undefined);
    try {
      // Check if we have the required environment variables for token exchange
      const exchangeTokenUrl = envConfig.VITE_AUTH_EXCHANGE_TOKEN_URL;
      if (!exchangeTokenUrl) {
        // VITE_AUTH_EXCHANGE_TOKEN_URL not configured, falling back to silent-login
        logger.warn('[IFrameAuthHandler] VITE_AUTH_EXCHANGE_TOKEN_URL not configured, falling back to silent-login');
        setIsExchangingToken(false);
        setExchangeError('Exchange token URL not configured');
        // Delay redirect to allow error state to be shown
        setTimeout(() => fallbackToSilentLogin(), 100);
        return;
      }

      logger.info('[IFrameAuthHandler] Exchanging access token for session cookie', {
        exchangeTokenUrl,
      });

      await Post(
        exchangeTokenUrl,
        '',
        { access_token: token || '' },
        { headers: { 'Content-Type': 'application/json' } }
      );

      logger.info('[IFrameAuthHandler] Token exchange successful, reloading page to establish session');
      // After successful token exchange, reload the page to establish the session
      // The session cookie will be set by the backend, and after reload,
      // hasValidSession will be true, allowing the app to proceed normally
      onReload();
    } catch (error: unknown) {
      setIsExchangingToken(false);
      let errorMessage = 'Failed to exchange token';
      if (error instanceof FetchError) {
        logger.error(new Error('[IFrameAuthHandler] Failed to exchange token'), {
          error: error.message,
          status: error.response.status,
        });
        if (error.response.status === 400) {
          errorMessage = 'Invalid request. Access token is required.';
          logger.warn('[IFrameAuthHandler] Invalid request. Access token is required.');
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid access token.';
          logger.warn('[IFrameAuthHandler] Invalid access token.');
        }
      } else {
        logger.error(new Error('[IFrameAuthHandler] Failed to exchange token'), {
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
      setExchangeError(errorMessage);
      // Failed to exchange IFrame token for session, fallback to silent-login
      // Delay redirect to allow error state to be shown
      setTimeout(() => fallbackToSilentLogin(), 100);
    }
  }, [token, fallbackToSilentLogin, onReload]);

  useEffect(() => {
    // Don't process if we already have a valid session
    if (hasValidSession) {
      return;
    }

    // Only process token exchange if we're in an IFrame and have a token
    // Don't retry if we already have an error (token or exchange)
    if (!isInIFrame() || !token || isExchangingToken || error) {
      return;
    }

    exchangeTokenForSession();
  }, [shouldWaitForToken, token, hasValidSession, isExchangingToken, exchangeTokenForSession, isInIFrame, error]);

  // Show loading/error state while waiting for token or exchanging token
  if (shouldWaitForToken) {
    return <IFrameLoading error={error} onRetry={onReload} />;
  }

  // If we're in an IFrame but have a valid session, or if we're not in an IFrame, render children
  return <>{children}</>;
};
